if (!customElements.get('andcharlie-product-size-dialog')) {
  customElements.define('andcharlie-product-size-dialog', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      this.root = this.closest('product-info');
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-ac-size-open]');
      this.status = this.querySelector('[data-ac-size-selection-status]');
      if (!this.root || !this.dialog?.showModal) return;
      this.labels = JSON.parse(this.querySelector('[data-ac-size-dialog-labels]').textContent);
      this.panel = document.getElementById(this.dataset.panelId);
      if (!this.panel) return;
      if (!this.home?.isConnected) {
        this.home = document.createComment('Product size panel returns here after closing.');
        this.panel.before(this.home);
      }
      const options = { signal: this.controller.signal };
      const open = event => {
        event.preventDefault();
        if (this.dialog.open) return;
        this.opener.removeAttribute('data-ac-size-pointer-return');
        this.pointerInteraction = false;
        this.pointerReturn = false;
        this.lastOpener = event.currentTarget;
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.movePanel(this.querySelector('[data-ac-size-dialog-body]'));
        this.dialog.showModal();
        this.refreshActions();
        this.querySelector('[data-ac-size-dialog-body]').scrollTop = 0;
        this.revealResult();
      };
      this.opener.addEventListener('click', open, options);
      const clearPointerReturn = () => this.opener.removeAttribute('data-ac-size-pointer-return');
      this.opener.addEventListener('blur', clearPointerReturn, options);
      this.opener.addEventListener('keydown', clearPointerReturn, options);
      this.dialog.addEventListener('pointerdown', () => { this.pointerInteraction = true; }, options);
      this.querySelector('[data-ac-size-close]').addEventListener('click', event => this.close(event), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('keydown', event => {
        this.pointerInteraction = false;
        if (event.key !== 'Tab') return;
        const controls = Array.from(this.dialog.querySelectorAll('button, a[href], input, select, textarea, summary, [tabindex]'))
          .filter(control => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(event); }, options);
      this.dialog.addEventListener('close', () => {
        if (this.home.parentNode) this.movePanel(this.home.parentNode, this.home.nextSibling);
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.opener.toggleAttribute('data-ac-size-pointer-return', this.pointerReturn);
        (this.lastOpener || this.opener).focus({ preventScroll: true });
      }, options);
      this.panel.addEventListener('ac:fit-result', event => {
        this.finder = event.target;
        this.candidateKeys = event.detail.candidateKeys;
        this.refreshActions();
      }, options);
      this.panel.addEventListener('submit', event => {
        if (event.target === this.finder?.form) this.revealResult();
      }, options);
      this.root.addEventListener('ac:size-intent', () => this.refreshActions(), options);
      this.root.addEventListener('change', () => queueMicrotask(() => this.refreshActions()), options);
      this.root.addEventListener('product-info:loaded', () => this.refreshActions(), options);
      this.observer = new MutationObserver(() => this.refreshActions());
      this.observer.observe(this.root, { attributes: true, attributeFilter: ['data-variant-state'] });
      this.finder = this.panel.querySelector('andcharlie-size-finder');
      this.finder?.result?.querySelector('[data-ac-fit-actions]')?.remove();
      if (this.finder?.payload && this.finder.result?.textContent) {
        const result = this.finder.resolve(this.finder.profile());
        if (result?.status === 'ready') this.candidateKeys = result.rankedCandidates;
      }
      this.refreshActions();
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow || '';
      }
      this.controller?.abort();
      this.observer?.disconnect();
    }
    movePanel(parent, before = null) {
      // Keep the same inputs, result and pet-record listeners while reparenting.
      // Regular section replacement still runs the finder's normal lifecycle.
      const finders = this.panel.querySelectorAll('andcharlie-size-finder');
      finders.forEach(finder => { finder.acSizeRelocating = true; });
      try { parent.insertBefore(this.panel, before); }
      finally { finders.forEach(finder => { finder.acSizeRelocating = false; }); }
    }
    close(event) {
      // Native focus restoration can retain an input's focus ring after a tap.
      // Keep the return target focused; only pointer dismissal suppresses its ring.
      this.pointerReturn = Boolean(event?.pointerType || event?.detail > 0 || this.pointerInteraction);
      this.opener.toggleAttribute('data-ac-size-pointer-return', this.pointerReturn);
      if (this.dialog.open) this.dialog.close();
      else this.opener.focus();
    }
    revealResult() {
      const result = this.finder?.result;
      if (!this.dialog.open || !result?.textContent) return;
      result.tabIndex = -1;
      result.focus({ preventScroll: true });
      const body = this.querySelector('[data-ac-size-dialog-body]');
      this.dialog.scrollTop = 0;
      body.scrollTop += result.getBoundingClientRect().top - body.getBoundingClientRect().top;
    }
    sizeInput(label) {
      return Array.from(this.root.querySelectorAll('variant-selects [data-ac-size-option] [data-option-value-id]'))
        .find(input => input.value === label);
    }
    availability(label) {
      if (['loading', 'error'].includes(this.root.dataset.variantState)) return 'pending';
      return this.sizeInput(label)?.dataset.acCombination || 'missing';
    }
    refreshActions() {
      if (!this.finder?.result || !Array.isArray(this.candidateKeys) || !this.finder.result.textContent) return;
      const current = this.finder.resolve(this.finder.profile());
      if (current?.status !== 'ready' || current.rankedCandidates.join('|') !== this.candidateKeys.join('|')) return;
      let actions = this.finder.result.querySelector('[data-ac-fit-actions]');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'ac-product-size-actions';
        actions.dataset.acFitActions = '';
        this.finder.result.append(actions);
      }
      const labels = this.candidateKeys.map(key => this.finder.payload.system.chart.rows.find(row => row.key === key)).filter(Boolean).map(row => this.finder.labelFor(row));
      const signature = labels.map(label => label + ':' + this.availability(label)).join('|');
      if (actions.dataset.state === signature) return;
      actions.dataset.state = signature;
      actions.replaceChildren();
      if (this.finder.dataset.acFitReference === 'reference') {
        const title = this.finder.result.querySelector('.ac-size-finder__result-label');
        if (title) title.textContent = this.labels.reference;
      }
      for (const label of labels) {
        const availability = this.availability(label);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary';
        button.dataset.acFitChoose = label;
        button.textContent = label + ' — ' + this.labels.pick;
        button.disabled = availability !== 'available';
        button.addEventListener('click', event => this.choose(event, label), { signal: this.controller.signal });
        actions.append(button);
        if (availability !== 'available') {
          const text = document.createElement('p');
          text.className = 'ac-size-finder__hint';
          text.textContent = label + '：' + (availability === 'soldout' ? this.labels.unavailable : availability === 'missing' ? this.labels.missing : this.labels.busy);
          actions.append(text);
        }
      }
    }
    choose(event, label) {
      if (this.availability(label) !== 'available') return;
      const input = this.sizeInput(label);
      if (!input || !this.root.acSizeIntent?.confirmRecommendation(event, input)) return;
      if (input.tagName === 'OPTION') {
        input.selected = true;
        input.closest('select').dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.status.textContent = label + '：' + this.labels.selected;
      this.close(event);
    }
  });
}
